import React from 'react'
import { Layout, Card, Switch, Button, Tag, Upload, Modal, Input, Spin, Popconfirm, message } from 'antd'
import flyersService from '@services/flyers'
import FlyerModal, { hebrewNames } from './FlyerModal'
import { MediwhoAppPageHeader, rtlLayoutStyle } from '../MediwhoApp/shared/mediwhoAppStyles'

// a template from GET /api/flyers/all (the boxes and fonts are used by FlyerModal)
interface Flyer {
    id: number
    name: string
    url: string
    thumbUrl: string
    is_active: number
    is_draft: number
    [setting: string]: any
}

// antd puts a margin-left after the icon and between buttons, the buttons sit together without it
const noMargin = { marginLeft: 0 }

// shows an error when the file is not a JPEG
const checkJpeg = (file: File) => {
    if (file.type !== 'image/jpeg') message.error('אפשר להעלות רק קובץ JPG')
    return file.type === 'image/jpeg'
}

class FlyersPage extends React.Component<any, any> {
    state = {
        flyers: [] as Flyer[],
        loading: false,
        editing: null as Flyer | null,
        addOpen: false,
        addName: '',
        addFile: null as File | null,
        adding: false,
        hideMode: false
    }

    componentDidMount() {
        this.load()
    }

    load = async () => {
        this.setState({ loading: true })
        try {
            const res = await flyersService.list()
            this.setState({ flyers: res.data })
        } catch (e) {
            message.error('שגיאה בטעינת הפליירים')
        } finally {
            this.setState({ loading: false })
        }
    }

    // sends a partial update (a toggle or a new image) and reloads
    patch = async (flyer: Flyer, fields: Record<string, string | File>) => {
        const body = new FormData()
        Object.keys(fields).forEach(key => body.append(key, fields[key]))
        try {
            await flyersService.update(flyer.id, body)
            this.load()
        } catch (e) {
            message.error('שגיאה בשמירת הפלייר')
        }
    }

    onToggle = (flyer: Flyer, checked: boolean) => this.patch(flyer, { is_active: checked ? '1' : '0' })

    // drafts become active, agents see them
    onPublish = async () => {
        try {
            await flyersService.publish()
            message.success('הפליירים החדשים נשמרו וגלויים לסוכנים')
            this.load()
        } catch (e) {
            message.error('שגיאה בשמירת הפליירים החדשים')
        }
    }

    onDelete = async (flyer: Flyer) => {
        try {
            await flyersService.remove(flyer.id)
            message.success('הפלייר נמחק')
            this.load()
        } catch (e) {
            message.error('שגיאה במחיקת הפלייר')
        }
    }

    onReplace = (flyer: Flyer, file: File) => {
        if (checkJpeg(file)) this.patch(flyer, { file })
        return false
    }

    // called by the edit modal, returns true when it saved
    onSaveEdit = async (id: number, body: FormData): Promise<boolean> => {
        try {
            await flyersService.update(id, body)
        } catch (e) {
            message.error('שגיאה בשמירת הפלייר')
            return false
        }
        message.success('הפלייר עודכן')
        this.setState({ editing: null })
        this.load()
        return true
    }

    closeAdd = () => this.setState({ addOpen: false, addName: '', addFile: null })

    onAdd = async () => {
        const { addName, addFile } = this.state
        if (!addFile) return
        const body = new FormData()
        body.append('name', addName.trim())
        body.append('file', addFile)
        this.setState({ adding: true })
        try {
            await flyersService.create(body)
            message.success('הפלייר נוסף')
            this.closeAdd()
            this.load()
        } catch (e) {
            message.error('שגיאה בהוספת הפלייר')
        } finally {
            this.setState({ adding: false })
        }
    }

    renderCard = (flyer: Flyer) => {
        const { hideMode } = this.state
        const draft = !!flyer.is_draft
        // a draft is not faded, it waits for the publish button
        const hidden = !flyer.is_active && !draft
        return (
            <Card
                key={flyer.id}
                size="small"
                style={{ opacity: hidden ? 0.5 : 1, border: draft ? '2px dashed #fa8c16' : undefined }}
                cover={
                    // gray box while loading, a broken image shows its alt text (the name)
                    // the box is always 330px (gray while loading), so the white strip at the bottom of a taller image is cut off
                    <div style={{ overflow: 'hidden', height: 330, background: '#f0f0f0' }}>
                        <img key={flyer.thumbUrl} src={flyer.thumbUrl} alt={flyer.name} style={{ display: 'block', width: '100%' }} />
                    </div>
                }
                actions={[
                    // one action item so both buttons sit together in the center
                    <div key="actions" style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                        <Upload accept="image/jpeg" showUploadList={false} beforeUpload={(file: File) => this.onReplace(flyer, file)}>
                            <Button icon="upload" style={noMargin}>
                                {/* own span so the margin-left antd puts after the icon can be reset */}
                                <span style={noMargin}>החלפת תמונה</span>
                            </Button>
                        </Upload>
                        <Button icon="edit" style={noMargin} onClick={() => this.setState({ editing: flyer })}>
                            <span style={noMargin}>עריכה</span>
                        </Button>
                        <Popconfirm
                            title="למחוק את הפלייר? גם הפליירים שהסוכנים יצרו ממנו יימחקו"
                            okText="מחיקה"
                            cancelText="ביטול"
                            onConfirm={() => this.onDelete(flyer)}
                        >
                            <Button icon="delete" type="danger" style={noMargin} />
                        </Popconfirm>
                    </div>
                ]}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    {/* name and its tag side by side, close together */}
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                        <span style={{ fontWeight: 600 }}>{hebrewNames[flyer.name] || flyer.name}</span>
                        {draft && (
                            <Tag color="orange" style={{ margin: 0 }}>
                                טיוטה – לא הושלם
                            </Tag>
                        )}
                        {hidden && <Tag style={{ margin: 0 }}>מוסתר</Tag>}
                    </div>
                    {hideMode && <Switch checked={!!flyer.is_active} onChange={(v: boolean) => this.onToggle(flyer, v)} />}
                </div>
            </Card>
        )
    }

    render() {
        const { flyers, loading, editing, addOpen, addName, addFile, adding, hideMode } = this.state
        return (
            <Layout style={rtlLayoutStyle}>
                <MediwhoAppPageHeader
                    title="פליירים"
                    subtitle={loading && !flyers.length ? 'טוען...' : flyers.length + ' תבניות'}
                    actionLabel="+ הוספת פלייר"
                    onAction={() => this.setState({ addOpen: true })}
                    extraActions={
                        <span>
                            {flyers.some(f => f.is_draft) && (
                                // green, the theme primary color is orange like the draft tag
                                <Button
                                    icon="check"
                                    onClick={this.onPublish}
                                    style={{ marginLeft: 8, background: '#52c41a', borderColor: '#52c41a', color: '#fff' }}
                                >
                                    שמירת פליירים חדשים
                                </Button>
                            )}
                            <Button
                                icon={hideMode ? 'close' : 'eye-invisible'}
                                style={{ marginLeft: 8 }}
                                onClick={() => this.setState({ hideMode: !hideMode })}
                            >
                                {hideMode ? 'ביטול הסתרה' : 'הסתרת פליירים'}
                            </Button>
                        </span>
                    }
                />
                <div style={{ padding: 24, direction: 'rtl' }}>
                    {loading && !flyers.length ? null : (
                        // 5 cards per row, each as wide as one of 5.5 columns: (100% - 64px) / 5.5
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(5, calc((100% - 64px) / 5.5))',
                                gap: 30,
                                width: '100%'
                            }}
                        >
                            {flyers.map(flyer => this.renderCard(flyer))}
                        </div>
                    )}
                </div>
                {editing && <FlyerModal flyer={editing} onClose={() => this.setState({ editing: null })} onSave={this.onSaveEdit} />}
                <Modal
                    visible={addOpen}
                    title="הוספת פלייר"
                    okText="הוספה"
                    cancelText="ביטול"
                    onCancel={this.closeAdd}
                    onOk={this.onAdd}
                    okButtonProps={{ disabled: !addName.trim() || !addFile, loading: adding }}
                >
                    <div style={{ direction: 'rtl' }}>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>שם הפלייר</div>
                        <Input
                            placeholder="שם הפלייר"
                            value={addName}
                            onChange={e => this.setState({ addName: e.target.value })}
                            style={{ marginBottom: 16 }}
                        />
                        <Upload
                            accept="image/jpeg"
                            showUploadList={false}
                            beforeUpload={(file: File) => {
                                if (checkJpeg(file)) this.setState({ addFile: file })
                                return false
                            }}
                        >
                            <Button icon="upload">בחירת תמונה (JPG)</Button>
                        </Upload>
                        {addFile && <span style={{ marginRight: 8 }}>{addFile.name}</span>}
                    </div>
                </Modal>
            </Layout>
        )
    }
}

export default FlyersPage
